import { usePermissionsModal } from '@/composables/usePermissionsModal'
import { useSubscription } from '@/composables/useSubscription'
import { defu } from 'defu'
import { navigateTo, useCookie, useRuntimeConfig, useState } from 'nuxt/app'
import type { UseFetchOptions } from 'nuxt/app'
import { toValue, type MaybeRefOrGetter } from 'vue'
import { API_CONTEXTS, type ApiContext } from './types'

function getPermissionErrorMessage(response: any): string {
  return response.status === 403
    ? response._data?.permission?.visible_name || ''
    : ''
}

function isSubscriptionCapabilityError(response: { status: number; _data?: any }): string | null {
  if (response.status !== 403)
    return null
  const data = response._data
  if (!data || typeof data !== 'object')
    return null

  const code = data.code
    || data.error?.code
    || data.error?.type
  const capability = data.capability
    || data.error?.details?.capability
    || data.error?.details?.code

  if (code === 'SUBSCRIPTION_CAPABILITY_REQUIRED' || code === 'feature_unavailable')
    return typeof capability === 'string' && capability ? capability : 'unknown'

  return null
}

function invokeResponseErrorHandlers<T>(handlers: UseFetchOptions<T>['onResponseError'], context: unknown) {
  const list = Array.isArray(handlers) ? handlers : handlers ? [handlers] : []
  for (const handler of list)
    handler(context as never)
}

/** Единая низкоуровневая реализация API-запросов для всех контекстов доступа. */
export function request<T>(
  url: MaybeRefOrGetter<string>,
  options: UseFetchOptions<T> = {},
  apiContext: ApiContext,
) {
  const contextConfig = API_CONTEXTS[apiContext]
  const config = useRuntimeConfig()
  const permissionsModal = usePermissionsModal()
  const accessTokenCookie = useCookie<string | null>('access_token')
  const currentClientId = useState<string | number | null>(
    'currentClientId',
    () => process.client ? localStorage.getItem('current_client_id') : null,
  )

  const accessToken = process.client
    ? localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || ''
    : accessTokenCookie.value || ''
  const timeZone = process.client
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    : 'UTC'
  const socketId = process.client ? String((window as any).__supportEchoSocketId || '') : ''
  const userResponseErrorHandlers = options.onResponseError

  const defaults: UseFetchOptions<T> = {
    baseURL: config.public.apiBaseUrl,
    key: `${apiContext}:${toValue(url)}::client=${contextConfig.clientId ? currentClientId.value ?? 'none' : 'none'}::tz=${timeZone}`,
    headers: {
      Accept: 'application/json',
      'X-Timezone': timeZone,
      ...(contextConfig.auth && accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(contextConfig.clientId && currentClientId.value ? { 'X-Client-Id': String(currentClientId.value) } : {}),
      ...(contextConfig.auth && socketId ? { 'X-Socket-ID': socketId } : {}),
    },
  }

  const params = defu({
    ...options,
    onResponseError(fetchContext: unknown) {
      const { response } = fetchContext as { response: { status: number; _data?: unknown } }

      if (contextConfig.auth && response.status === 401) {
        if (process.client) {
          localStorage.removeItem('access_token')
          localStorage.removeItem('is_system')
          localStorage.removeItem('system_permissions')
        }
        accessTokenCookie.value = null
        void navigateTo('/login')
      }
      else if (contextConfig.auth && response.status === 403) {
        const capability = isSubscriptionCapabilityError(response)
        if (capability) {
          try {
            const { invalidate } = useSubscription()
            invalidate()
          }
          catch {
            /* composable may be unavailable outside setup */
          }
          void navigateTo({
            path: '/feature-locked',
            query: {
              capability: capability === 'unknown' ? undefined : capability,
              from: process.client ? window.location.pathname : undefined,
            },
          })
        }
        else {
          permissionsModal.show({
            code: response.status,
            title: 'Недостаточно прав',
            message: getPermissionErrorMessage(response),
          })
        }
      }

      invokeResponseErrorHandlers(userResponseErrorHandlers, fetchContext)
    },
  }, defaults)

  return useFetch(url, params)
}

export function createApiRequester(apiContext: ApiContext): typeof useFetch {
  return ((url: MaybeRefOrGetter<string>, options: UseFetchOptions<unknown> = {}) =>
    request(url, options, apiContext)) as typeof useFetch
}
